/**
 * Phase H1-η · AllocationBadge
 *
 * Renders the frozen visual language for allocation_type:
 *   EXACT (emerald), ALT / APPROVED_ALTERNATE (amber),
 *   DIRECT / DIRECT_PURCHASE (cyan), LEGACY (zinc).
 *
 * Colour is NEVER the sole indicator — every badge carries a text
 * label + aria-label so screen-readers surface the semantic value.
 *
 * Contract (frozen scope §11.1):
 *   <AllocationBadge type="EXACT" size="sm" />
 *   <AllocationBadge type="APPROVED_ALTERNATE" priority={1} size="md" />
 *   <AllocationBadge type="DIRECT_PURCHASE" />
 *   <AllocationBadge type="LEGACY" />
 */
import React from "react";

const NORMAL = {
  EXACT:              { label: "EXACT",    cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40" },
  APPROVED_ALTERNATE: { label: "ALT",      cls: "bg-amber-500/10 text-amber-300 border-amber-500/40"     },
  ALT:                { label: "ALT",      cls: "bg-amber-500/10 text-amber-300 border-amber-500/40"     },
  DIRECT_PURCHASE:    { label: "DIRECT",   cls: "bg-cyan-500/10 text-cyan-300 border-cyan-500/40"       },
  DIRECT:             { label: "DIRECT",   cls: "bg-cyan-500/10 text-cyan-300 border-cyan-500/40"       },
  LEGACY:             { label: "LEGACY",   cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/40"       },
};

const SIZES = {
  sm: "text-[9px] px-1.5 py-0.5",
  md: "text-[10px] px-2 py-0.5",
  lg: "text-[11px] px-2.5 py-1",
};

export default function AllocationBadge({ type, size = "md", priority, className = "" }) {
  const upper = String(type || "").toUpperCase();
  const cfg = NORMAL[upper] || NORMAL.LEGACY;
  const suffix = (upper === "APPROVED_ALTERNATE" || upper === "ALT") && priority
    ? ` P${priority}` : "";
  const label = cfg.label + suffix;
  const aria = `Allocation ${cfg.label}${suffix ? ", priority " + priority : ""}`;
  return (
    <span
      role="status"
      aria-label={aria}
      data-testid={`allocation-badge-${cfg.label.toLowerCase()}`}
      className={`inline-flex items-center rounded-sm border font-mono uppercase tracking-widest ${cfg.cls} ${SIZES[size] || SIZES.md} ${className}`}
    >
      {label}
    </span>
  );
}
